import { memo, type RefObject, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useRowHeights, useVirtualRows } from "@/hooks/useVirtualRows";
import type { AssetListItem, FolderItem } from "@/lib/api";
import { folderRoute } from "@/lib/folderUpload";
import { formatBytes, formatRelative } from "@/lib/format";
import { podeRenderizarComCamadas, podeTrocarViewer } from "@/lib/viewerKindOverride";
import { useAuthStore } from "@/state/authStore";
import { useFileManagerStore } from "@/state/fileManagerStore";

import { ContextMenu } from "./ContextMenu";
import { FavoriteStarButton } from "./FavoriteStarButton";
import { FormatBadge } from "./FormatBadge";
import { RenderModeBadge } from "./RenderModeBadge";
import { StatusBadge } from "./StatusBadge";
import { ThumbImage } from "./ThumbImage";
import { ViewerKindBadge } from "./ViewerKindBadge";
import { ViewerKindControl } from "./ViewerKindControl";
import { assetDpiDetail, assetFormatLabel, assetShowsChannelBadge, assetViewerKind } from "./assetMeta";

export type FileManagerItem =
  | { kind: "folder"; id: string; folder: FolderItem }
  | { kind: "asset"; id: string; asset: AssetListItem };

/** Linhas desenhadas antes da primeira medição — o bastante para encher a tela. */
const ROWS_BEFORE_MEASURE = 60;

interface FileManagerListProps {
  items: FileManagerItem[];
  /** O elemento que rola, para a janela virtual (A1). */
  scrollRef: RefObject<HTMLElement>;
  /** Muda a cada pasta/filtro/página: a rolagem volta ao topo. */
  resetKey: string;
  onRenameFolder: (folder: FolderItem) => void;
  onDeleteFolder: (folder: FolderItem) => void;
  onToggleFolderFavorite: (folder: FolderItem) => void;
  onRenameAsset: (asset: AssetListItem) => void;
  onDeleteAsset: (asset: AssetListItem) => void;
  onRenderAsset: (asset: AssetListItem, mode: "channels" | "composite", options?: { layers?: boolean }) => void;
  onCompareAsset: (asset: AssetListItem) => void;
  onCompareAssetVersion: (asset: AssetListItem) => void;
  /** Ausente para quem não é administrador — o item some do menu (F1.21). */
  onHistoryAsset?: (asset: AssetListItem) => void;
  /** Ausente para quem não é administrador — o item some do menu (F1.21). */
  onRenderLogsAsset?: (asset: AssetListItem) => void;
  onDownloadAsset: (asset: AssetListItem) => void;
  renderingAssetIds: Set<string>;
}

export const FileManagerList = memo(function FileManagerList({
  items,
  scrollRef,
  resetKey,
  onRenameFolder,
  onDeleteFolder,
  onToggleFolderFavorite,
  onRenameAsset,
  onDeleteAsset,
  onRenderAsset,
  onCompareAsset,
  onCompareAssetVersion,
  onHistoryAsset,
  onRenderLogsAsset,
  onDownloadAsset,
  renderingAssetIds,
}: FileManagerListProps) {
  // Janela virtual (A1): as pastas vêm primeiro e as artes depois, cada tipo
  // com a sua altura de linha; só as linhas perto do viewport existem no DOM
  // e o resto vira `padding` do tamanho exato. Os hooks ficam antes do
  // `return null` para a contagem de hooks não mudar entre lista vazia e cheia.
  const listRef = useRef<HTMLDivElement>(null);
  const heights = useRowHeights(listRef, items.length);
  let folderCount = 0;
  for (const item of items) {
    if (item.kind !== "folder") break;
    folderCount += 1;
  }
  const segments = heights
    ? [
        { count: folderCount, height: heights.folder, gap: 0 },
        { count: items.length - folderCount, height: heights.asset, gap: 0 },
      ]
    : [];
  const window = useVirtualRows({ scrollRef, containerRef: listRef, segments, overscan: 6, resetKey });

  if (items.length === 0) return null;

  let start = 0;
  let end = Math.min(items.length, ROWS_BEFORE_MEASURE);
  let before = 0;
  let after = 0;
  if (heights && window.rows > 0) {
    start = window.first;
    end = Math.min(items.length, window.last + 1);
    before = window.before;
    after = window.after;
  }

  return (
    <div
      ref={listRef}
      className="overflow-hidden rounded-lg border border-ink-700"
      style={{ paddingTop: before, paddingBottom: after }}
    >
      {items.slice(start, end).map((item) =>
        item.kind === "folder" ? (
          <FolderRow
            key={item.id}
            folder={item.folder}
            onRename={onRenameFolder}
            onDelete={onDeleteFolder}
            onToggleFavorite={onToggleFolderFavorite}
          />
        ) : (
          <AssetRow
            key={item.id}
            asset={item.asset}
            onRename={onRenameAsset}
            onDelete={onDeleteAsset}
            onRender={onRenderAsset}
            onCompare={onCompareAsset}
            onCompareVersion={onCompareAssetVersion}
            onHistory={onHistoryAsset}
            onRenderLogs={onRenderLogsAsset}
            onDownload={onDownloadAsset}
            rendering={renderingAssetIds.has(item.asset.id)}
          />
        ),
      )}
    </div>
  );
});

interface FolderRowProps {
  folder: FolderItem;
  onRename: (folder: FolderItem) => void;
  onDelete: (folder: FolderItem) => void;
  onToggleFavorite: (folder: FolderItem) => void;
}

const FolderRow = memo(function FolderRow({ folder, onRename, onDelete, onToggleFavorite }: FolderRowProps) {
  return (
    <div
      data-kind="folder"
      className="group grid gap-3 border-b border-ink-800 px-3 py-2 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_110px_90px] sm:items-center"
    >
      <Link
        to={folderRoute(folder.path)}
        className="flex min-w-0 items-center gap-3 rounded text-left hover:text-emerald-200"
      >
        <svg className="h-8 w-8 shrink-0 text-yellow-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        </svg>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium" title={folder.name}>
            {folder.name}
          </div>
          <div className="text-[10px] text-ink-500">Pasta</div>
        </div>
      </Link>
      <div className="text-xs text-ink-400">{folder.asset_count} arquivos</div>
      <div className="flex items-center justify-end gap-1">
        <FavoriteStarButton folder={folder} onToggle={onToggleFavorite} />
        <ContextMenu
          items={[
            {
              label: folder.favorite ? "Remover dos favoritos" : "Favoritar",
              onSelect: () => onToggleFavorite(folder),
            },
            { label: "Renomear", onSelect: () => onRename(folder) },
            { label: "Excluir", destructive: true, onSelect: () => onDelete(folder) },
          ]}
          ariaLabel={`Ações da pasta ${folder.name}`}
        />
      </div>
    </div>
  );
});

interface AssetRowProps {
  asset: AssetListItem;
  onRename: (asset: AssetListItem) => void;
  onDelete: (asset: AssetListItem) => void;
  onRender: (asset: AssetListItem, mode: "channels" | "composite", options?: { layers?: boolean }) => void;
  onCompare: (asset: AssetListItem) => void;
  onCompareVersion: (asset: AssetListItem) => void;
  onHistory?: (asset: AssetListItem) => void;
  onRenderLogs?: (asset: AssetListItem) => void;
  onDownload: (asset: AssetListItem) => void;
  rendering: boolean;
}

const AssetRow = memo(function AssetRow({
  asset,
  onRename,
  onDelete,
  onRender,
  onCompare,
  onCompareVersion,
  onHistory,
  onRenderLogs,
  onDownload,
  rendering,
}: AssetRowProps) {
  const selected = useFileManagerStore((state) => state.selectedAssetId === asset.id);
  const setSelectedAssetId = useFileManagerStore((state) => state.setSelectedAssetId);
  const formatLabel = assetFormatLabel(asset);
  // Mesmo critério do card: DPI é detalhe de produção, só para admin (F1.21).
  const isAdmin = useAuthStore((state) => state.user?.is_admin === true);
  const dpiDetail = isAdmin ? assetDpiDetail(asset) : null;
  const showChannelBadge = assetShowsChannelBadge(asset);
  const separationCount = asset.separation_count ?? null;
  const renderDisabled =
    rendering ||
    !asset.current_file_id ||
    asset.status === "queued" ||
    asset.status === "rendering" ||
    asset.status === "preparing";
  const [tipoDeViewerAberto, setTipoDeViewerAberto] = useState(false);

  return (
    <div
      data-kind="asset"
      className={`grid gap-3 border-b border-ink-800 px-3 py-2 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_120px_90px_96px] sm:items-center ${
        selected ? "bg-emerald-900/20" : "hover:bg-ink-800/40"
      }`}
    >
      <button
        type="button"
        onClick={() => setSelectedAssetId(asset.id)}
        className="flex min-w-0 items-center gap-3 rounded text-left"
      >
        <div className="flex h-10 w-8 shrink-0 items-center justify-center overflow-hidden rounded bg-ink-900">
          {asset.thumb_url ? (
            <ThumbImage
              src={asset.thumb_url}
              alt=""
              label={formatLabel}
              className="h-full w-full object-contain"
              width={80}
              height={100}
              compact
            />
          ) : (
            <span className="text-[9px] text-ink-600">{formatLabel}</span>
          )}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium" title={asset.path}>
            {asset.filename}
          </div>
          <div className="text-[10px] text-ink-500">
            {/* O arquivo é uma separação do job, e é assim que ele se chama: os
                sete ficam visíveis (§15 do plano), e o selo ao lado é que diz
                de quantas ele faz parte. */}
            {separationCount ? "Separação do conjunto" : `Arquivo ${formatLabel}`}
          </div>
        </div>
      </button>
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge status={asset.status} />
        <FormatBadge label={formatLabel} />
        <ViewerKindBadge kind={assetViewerKind(asset)} manual={Boolean(asset.viewer_kind_override)} />
        <RenderModeBadge mode={asset.render_mode} hasChannels={showChannelBadge} />
        {separationCount ? (
          <span
            className="rounded bg-ink-700 px-1.5 py-0.5 text-[10px] text-ink-200"
            title="Separações do Esko nesta pasta — abrir qualquer uma leva ao conjunto inteiro"
          >
            {separationCount} {separationCount === 1 ? "separação" : "separações"}
          </span>
        ) : null}
        {asset.notes_count > 0 && (
          <span className="rounded bg-ink-700 px-1.5 py-0.5 text-[10px] text-ink-200">
            {asset.notes_count} notas
          </span>
        )}
      </div>
      <div className="text-xs text-ink-400">
        <div>{formatBytes(asset.size_bytes)}</div>
        {dpiDetail ? <div className="text-[10px] text-ink-500">{dpiDetail}</div> : null}
        <div className="text-[10px] text-ink-500">{formatRelative(asset.last_accessed_at)}</div>
      </div>
      <div className="flex items-center justify-end gap-1">
        {asset.viewer_url && (
          <button
            type="button"
            onClick={() => window.open(asset.viewer_url!, "_blank", "noopener,noreferrer")}
            className="rounded border border-ink-700 px-2 py-1 text-xs text-ink-200 hover:bg-ink-800"
          >
            Abrir
          </button>
        )}
        {rendering && (
          <span className="text-[11px] text-ink-400 italic">Renderizando...</span>
        )}
        <ContextMenu
          items={[
            {
              label: rendering ? "Renderizando..." : "Renderizar",
              onSelect: () => {
                if (renderDisabled) return;
                onRender(asset, "channels");
              },
            },
            // Mesmo critério do card: PDF de embalagem.
            ...(podeRenderizarComCamadas(asset)
              ? [
                  {
                    label: "Renderizar com camadas",
                    onSelect: () => {
                      if (renderDisabled) return;
                      onRender(asset, "channels", { layers: true });
                    },
                  },
                ]
              : []),
            // Mesmo critério do card: quem vê o «Renderizar», e só em PDF.
            ...(podeTrocarViewer(asset)
              ? [{ label: "Tipo de viewer…", onSelect: () => setTipoDeViewerAberto(true) }]
              : []),
            { label: "Comparar", onSelect: () => onCompare(asset) },
            ...(asset.viewer_kind === "document"
              ? [
                  {
                    label: "Comparar com a versão anterior",
                    onSelect: () => onCompareVersion(asset),
                  },
                ]
              : []),
            { label: "Baixar original", onSelect: () => onDownload(asset) },
            // Histórico e logs são de administrador (F1.21): o FileManager
            // omite os handlers para quem não é, e o item some do menu.
            ...(onHistory ? [{ label: "Histórico", onSelect: () => onHistory(asset) }] : []),
            ...(onRenderLogs
              ? [{ label: "Logs do render", onSelect: () => onRenderLogs(asset) }]
              : []),
            { label: "Renomear", onSelect: () => onRename(asset) },
            { label: "Excluir", destructive: true, onSelect: () => onDelete(asset) },
          ]}
          ariaLabel={`Ações de ${asset.filename}`}
        />
        <ViewerKindControl
          asset={asset}
          open={tipoDeViewerAberto}
          onClose={() => setTipoDeViewerAberto(false)}
        />
      </div>
    </div>
  );
});
